import * as authApi from '@/api/auth';

export const useAuthStore = createGlobalState(() => {
  const state = useStorage<{ isAuthorized: boolean }>('authState', {
    isAuthorized: false,
  });

  const isAuthorized = computed(() => state.value.isAuthorized);

  const setIsAuthorized = (isAuthorized: boolean) => {
    state.value.isAuthorized = isAuthorized;
  };

  const getToken = async (credentials: {
    email: string;
    password: string;
    totpCode?: string;
    recoveryCode?: string;
  }) => {
    try {
      const data = await authApi.getToken(credentials);

      if (data && data.access) {
        setIsAuthorized(true);
      }
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const logout = () => {
    authApi.logout();
    setIsAuthorized(false);
  };

  const requestPasswordReset = async (email: string) => {
    try {
      await authApi.requestPasswordReset(email);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const confirmPasswordReset = async ({
    uid,
    token,
    newPassword,
  }: {
    uid: string;
    token: string;
    newPassword: string;
  }) => {
    try {
      await authApi.confirmPasswordReset({ uid, token, newPassword });
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const passwordChange = async (oldPassword: string, newPassword: string) => {
    try {
      await authApi.passwordChange(oldPassword, newPassword);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const setupTotp = async () => {
    try {
      return authApi.setupTotp();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const activateTotp = async (code: string) => {
    try {
      return await authApi.activateTotp(code);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const verifyTotp = async (
    scope: string,
    code?: string,
    recoveryCode?: string,
  ) => {
    try {
      await authApi.verifyTotp(scope, code, recoveryCode);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const disableTotp = async () => {
    try {
      await authApi.disableTotp();
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  return {
    getToken,
    logout,
    isAuthorized,
    setIsAuthorized,
    requestPasswordReset,
    confirmPasswordReset,
    passwordChange,
    setupTotp,
    activateTotp,
    verifyTotp,
    disableTotp,
  };
});
